import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import { MenuCsv, MenuDetail } from 'features/SubTitleMenu/models/SubTitleMenu';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';
import { TfiReload } from 'react-icons/tfi';
import { STATUS_ERROR, STATUS_SUCCESS } from 'utils';
import { csvToJSON } from 'utils/csvToArrayJson';
import DetailModal from '../../detail/Detail';
import service from '../services';
import ExportCSV from './ExportCSV';
import CSVDownloader from './ExportCSVTemplate';
// define props
interface DataTableProps {
  dataTable: MenuDetail[];
  setDataTable: (dataTable: MenuDetail[]) => void;
  handleReload: () => void;
}

function TableList({ dataTable, setDataTable, handleReload }: DataTableProps) {
  const { t } = useTranslation();
  const initialState = dataTable.reduce((o, key) => ({ ...o, [key.menu.id]: false }), {});
  const [checked, setChecked] = useState<any>(initialState);
  const refBtnDetail = useRef<HTMLButtonElement>(null);
  const refBtnDelete = useRef<HTMLButtonElement>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [idDetail, setIdDetail] = useState<string>('');
  const { showGlobalModal } = useGlobalModal();
  const [subTitleMenuChoose, setSubTitleMenuChoose] = useState<MenuDetail[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUploadAction = () => {
    fileInputRef.current?.click();
  };

  const keys = Object.keys(checked);
  // handle checked all
  function handleCheckedAll() {
    let newState: any = {};
    keys.forEach(function (key) {
      newState[key] = true;
    });
    setChecked(newState);
    setSubTitleMenuChoose(dataTable);
  }

  // handle unchecked all state changes
  function handleUncheckedAll() {
    let newState: any = {};
    keys.forEach(function (key) {
      newState[key] = false;
    });
    setChecked(newState);
    setSubTitleMenuChoose([]);
  }

  // toggle checkbox
  function toggleCheck(value: number) {
    let newState = { ...checked };
    newState[value] = !newState[value];
    setChecked(newState);
  }

  // handle reload
  async function handleReloadTable() {
    if (handleReload) {
      handleReload();
      handleUncheckedAll();
    }
  }

  // useEffects check change checked
  useEffect(() => {
    // handle disabled btn view detail
    function handleDisableBtnDelte() {
      let count = Object.keys(checked).filter((key) => checked[key] === true).length;
      if (count > 0) {
        return false;
      }
      return true;
    }

    // handle disabled btn view detail
    function handleDisableBtnViewDetail() {
      let count = Object.keys(checked).filter((key) => checked[key] === true).length;
      if (count > 1 || count === 0) {
        return true;
      }
      return false;
    }

    if (handleDisableBtnViewDetail()) {
      if (refBtnDetail.current) refBtnDetail.current.disabled = true;
    } else {
      if (refBtnDetail.current) refBtnDetail.current.disabled = false;
    }
    if (handleDisableBtnDelte()) {
      if (refBtnDelete.current) refBtnDelete.current.disabled = true;
    } else {
      if (refBtnDelete.current) refBtnDelete.current.disabled = false;
    }

    let ids = Object.keys(checked).filter((key) => checked[key] === true);
    const menuFilters = dataTable.filter((item) => ids.includes(item.menu.id.toString()));
    setSubTitleMenuChoose(menuFilters);
    setIdDetail(ids[0]);
  }, [checked, dataTable]);

  // handle function go to default page
  function handleDefaultShowDetailMenu() {
    setShowEditModal(true);
  }

  async function handleImportCSV(event: React.ChangeEvent<HTMLInputElement>) {
    event.preventDefault();
    if (fileInputRef.current) {
      if (event.target.files && event.target.files.length) {
        const fileExtension = event.target.files[0]?.type.split('/')[1];
        if (fileExtension !== 'csv') {
          // Reset input value to allow selecting the same file again
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          return;
        }
        const reader = new FileReader();
        reader.onload = async function (event) {
          const text = event?.target?.result as string;
          const data = csvToJSON(text) as MenuCsv[];
          await service
            .importSubTitleMenu(data)
            .then((res) => {
              if (res.status === STATUS_SUCCESS) {
                showGlobalModal(() => <AlertModal 
                  message={res.message} 
                  title={t('App.info')} 
                  size="lg" 
                  onClose={handleReloadTable}
                />);
              } else if (res.status === STATUS_ERROR) {
                showGlobalModal(() => <AlertModal message={res.message} title={t('App.error')} />);
              }
              // Reset input value after processing
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
            })
            .catch((res: AxiosError) => {
              const data: any = res?.response?.data;
              if (data?.status === STATUS_ERROR) {
                showGlobalModal(() => <AlertModal message={res.message} title={t('App.error')} />);
              }
              // Reset input value even on error
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
            });
        };
        reader.readAsText(event.target.files[0]);
      }
    }
  }

  // delete muilti record
  async function handleClickBtnDeleteAll() {
    let idsArr = Object.keys(checked).filter((key) => {
      if (checked[key] === true) {
        return parseInt(key);
      }
      return null;
    });
    let ids = idsArr
      .map((value) => {
        return parseInt(value);
      })
      .join(',');
    await service
      .deleteSubTitleMenus(ids)
      .then(async () => {
        showGlobalModal &&
          showGlobalModal(() => (
            <AlertModal
              onClose={handleReloadTable}
              title={t('SubTitleMenu.Validator.success')}
              message={t('SubTitleMenu.Validator.delete_success')}
            ></AlertModal>
          ));
      })
      .catch((err: AxiosError) => {
        <AlertModal title={t('SubTitleMenu.Validator.error')} message={t('SubTitleMenu.Validator.500')}></AlertModal>;
      });
  }

  // close modal edit
  function handleCloseModalEdit() {
    setShowEditModal(false);
    handleUncheckedAll();
  }

  async function handleRestore(id: number) {
    await service
      .restoreDelete(id)
      .then(async () => {
        showGlobalModal &&
          showGlobalModal(() => (
            <AlertModal
              onClose={handleReloadTable}
              title={t('SubTitleMenu.Validator.success')}
              message={t('SubTitleMenu.Validator.restore_success')}
            ></AlertModal>
          ));
      })
      .catch((err: any) => {
        <AlertModal title={t('SubTitleMenu.Validator.error')} message={t('SubTitleMenu.Validator.500')}></AlertModal>;
      });
  }
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: string }>({ key: '', direction: '' });
  const requestSort = (key: string) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };
  
  const getSortIcon = (key: string) => {
    // Default: if no sort key, highlight start_date as descending
    if (!sortConfig.key && key === 'start_date') {
      return <FaSortDown size={16} />;
    }
    if (sortConfig.key !== key)
      return (
      <>
        <FaSort size={16} />
      </>
      );
    return sortConfig.direction === 'ascending' ? <FaSortUp size={16} /> : <FaSortDown size={16} />;
  };
  const sortedMenuDetails = useMemo(() => {
      let sortableItems = [...dataTable];
      if (sortConfig.key) {
        sortableItems.sort((a, b) => {
          let aValue, bValue;
          switch (sortConfig.key) {
            case 'menu_name':
              const aMenuName = a.menu?.food_system_menu?.name || '';
              const bMenuName = b.menu?.food_system_menu?.name || '';
              return sortConfig.direction === 'ascending'
                ? aMenuName.localeCompare(bMenuName, 'ja')
                : bMenuName.localeCompare(aMenuName, 'ja');
            case 'menu_code':
              aValue = a.menu?.food_system_menu?.cd;
              bValue = b.menu?.food_system_menu?.cd;
              return sortConfig.direction === 'ascending'
                ? String(aValue).localeCompare(String(bValue))
                : String(bValue).localeCompare(String(aValue));
            case 'menu_type':
              aValue = a.menu.type === 3 ? 1 : 0;
              bValue = b.menu.type === 3 ? 1 : 0;
              return sortConfig.direction === 'ascending'
                ? Number(aValue) - Number(bValue)
                : Number(bValue) - Number(aValue);
            case 'start_date':
              aValue = a.start_date;
              bValue = b.start_date;
              // Compare dates
              return sortConfig.direction === 'ascending'
                ? new Date(aValue).getTime() - new Date(bValue).getTime()
                : new Date(bValue).getTime() - new Date(aValue).getTime();
            case 'end_date':
              aValue = a.end_date;
              bValue = b.end_date;
              // Compare dates
              return sortConfig.direction === 'ascending'
                ? new Date(aValue).getTime() - new Date(bValue).getTime()
                : new Date(bValue).getTime() - new Date(aValue).getTime();
            case 'pos_code':
              aValue = a.pos_code?.cd || '';
              bValue = b.pos_code?.cd || '';
              return sortConfig.direction === 'ascending'
                ? aValue.localeCompare(bValue, 'ja')
                : bValue.localeCompare(aValue, 'ja');
            default:
              return 0;
          }
        });
      }
      return sortableItems;
    }, [dataTable, sortConfig]);
  return (
    <>
      <Row className="mb-2">
        <Col sm={12} md={12} lg={12} className="d-flex justify-content-end">
          <button type="button" className="btn btn-success" onClick={handleFileUploadAction}>
            {t('SubTitleMenu.Button.import_csv')}
          </button>
          <input type="file" hidden ref={fileInputRef} onChange={handleImportCSV} accept=".csv" />
        </Col>
      </Row>
      <Row className="mb-4">
        <Col sm={12} md={12} lg={12} className="d-flex justify-content-end">
          <CSVDownloader></CSVDownloader>
        </Col>
      </Row>
      <Row className="mb-2">
        <Col sm={2} lg={2}>
          <button className="link" onClick={handleCheckedAll}>
            {t('SubTitleMenu.Button.select_all')}
          </button>{' '}
          /{' '}
          <button className="link" onClick={handleUncheckedAll}>
            {t('SubTitleMenu.Button.cancel_all')}
          </button>
          <span className="ps-2 pe-2"></span>
          <button type="button" onClick={handleReloadTable} className="btn-primary btn-circle">
            <TfiReload />
          </button>
          {t('SubTitleMenu.Button.reload')}
        </Col>
        <Col sm={10} lg={10} className="d-flex justify-content-end">
          <button ref={refBtnDetail} type="button" className="btn btn-primary" onClick={handleDefaultShowDetailMenu}>
            {t('SubTitleMenu.Button.detail')}
          </button>
          <DetailModal
            id={idDetail}
            show={showEditModal}
            onClose={handleCloseModalEdit}
            dataTable={dataTable}
            setDataTable={setDataTable}
          />
          <span className="ps-2 pe-2"></span>
          <button type="button" ref={refBtnDelete} className="btn btn-danger" onClick={handleClickBtnDeleteAll}>
            {t('SubTitleMenu.Button.delete')}
          </button>
          <span className="ps-2 pe-2"></span>
          <div className="btn btn-success btn-wrap-inline">
            <ExportCSV subTitleMenus={subTitleMenuChoose}></ExportCSV>
          </div>
        </Col>
      </Row>
      <div className="sticky-table" style={{ maxHeight: '500px' }}>
        <Table bordered className="table table-hover subtitle-menu">
          <thead className="text-center" style={{ backgroundColor: '#36489a', color: '#fff' }}>
            <tr>
              <th rowSpan={2} className="align-middle" style={{ width: '5%' }}>
                {t('SubTitleMenu.Table.selection')}
              </th>
              <th rowSpan={2} className="align-middle cursor-pointer" style={{ width: '30%' }} onClick={() => requestSort('menu_code')}>
                {t('SubTitleMenu.Table.code')}
                {getSortIcon('menu_code')}
              </th>
              <th rowSpan={2} className="align-middle cursor-pointer" onClick={() => requestSort('menu_name')}>
                {t('SubTitleMenu.Table.name')}
                {getSortIcon('menu_name')}
              </th>
              <th rowSpan={2} className="align-middle cursor-pointer" onClick={() => requestSort('menu_type')}>
                {t('SubTitleMenu.Table.type')}
                {getSortIcon('menu_type')}
              </th>
              <th rowSpan={1} colSpan={2} className="align-middle ">
                {t('SubTitleMenu.Table.date_of_expiry')}
              </th>
              <th colSpan={1} className="align-middle">
                {t('SubTitleMenu.Table.info')}
              </th>
            </tr>
            <tr>
              <th className="align-middle cursor-pointer" onClick={() => requestSort('start_date')}>
                {t('SubTitleMenu.Table.start_date')}
                {getSortIcon('start_date')}
              </th>
              <th className="align-middle cursor-pointer" onClick={() => requestSort('end_date')}>
                {t('SubTitleMenu.Table.end_date')}
                {getSortIcon('end_date')}
              </th>
              <th className="align-middle cursor-pointer" onClick={() => requestSort('pos_code')}>
                {t('SubTitleMenu.Table.pos_code')}
                {getSortIcon('pos_code')}
              </th>
            </tr>
          </thead>
          <tbody className="text-center">
            {sortedMenuDetails &&
              sortedMenuDetails.map((value: MenuDetail) => (
                <tr key={value.id} className={value.delete_flg ? 'deleted_row ' : ''}>
                  <td>
                    {!value.delete_flg ? (
                      <Form.Check
                        type="checkbox"
                        value={value.menu.id}
                        checked={checked[value.menu.id]}
                        disabled={false}
                        onChange={() => toggleCheck(value.menu.id)}
                      ></Form.Check>
                    ) : (
                      <Button
                        variant="warning"
                        onClick={() => {
                          handleRestore(value.menu.id);
                        }}
                      >
                        {t('SubTitleMenu.Button.restore')}
                      </Button>
                    )}
                  </td>
                  <td>{value.menu?.food_system_menu?.cd}</td>
                  <td>{value.menu?.food_system_menu?.name}</td>
                  <td>{value.menu.type === 3 ? '〇' : ''}</td>
                  <td>{new Date(value.start_date).toLocaleDateString('ja-JP', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    })}</td>
                  <td>
                    {new Date(value.end_date).toLocaleDateString('ja-JP', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    })}
                  </td>
                  <td>{value.pos_code?.cd}</td>
                </tr>
              ))}
          </tbody>
        </Table>
      </div>
    </>
  );
}

export default TableList;
